import { useState, useRef, useEffect, type ReactNode, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/utils/helpers';

/* ═══════════════════════════════════════════════════════════════════════════ */
/* STAT CARD                                                                  */
/* ═══════════════════════════════════════════════════════════════════════════ */

export function StatCard({ label, value, change, changeType = 'neutral', icon, delay = 0, tooltip }: {
  label: string; value: string | number; change?: string;
  changeType?: 'positive' | 'negative' | 'neutral'; icon: ReactNode; delay?: number;
  /** Native browser tooltip — shown on hover so we can clarify scope (e.g. "Storage = receipts + scanned docs only"). */
  tooltip?: string;
}) {
  return (
    <div className="glass-panel p-5 animate-slide-up opacity-0 transition-all duration-300 hover:shadow-card-hover hover:-translate-y-0.5"
      style={{ animationDelay: `${delay}ms`, animationFillMode: 'forwards' }}
      title={tooltip}>
      <div className="flex items-start justify-between mb-3">
        <div className="p-2 rounded-xl bg-brand-500/8 text-brand-500">{icon}</div>
        {change && (
          <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full',
            changeType === 'positive' && 'badge-emerald',
            changeType === 'negative' && 'badge-red',
            changeType === 'neutral' && 'bg-surface-100 text-surface-600',
          )}>{change}</span>
        )}
      </div>
      <div className="text-2xl font-bold text-surface-900 tracking-tight">{value}</div>
      <div className="text-sm text-surface-500 mt-0.5">{label}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* STATUS BADGE                                                               */
/* ═══════════════════════════════════════════════════════════════════════════ */

const STATUS_STYLES: Record<string, { classes: string; dot: string }> = {
  active:    { classes: 'badge-emerald', dot: 'bg-emerald-500' },
  trial:     { classes: 'badge-amber',   dot: 'bg-amber-500' },
  suspended: { classes: 'badge-red',     dot: 'bg-red-500' },
  cancelled: { classes: 'bg-surface-200 text-surface-500', dot: 'bg-surface-400' },
};

export function StatusBadge({ status }: { status: string }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.cancelled;
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium', s.classes)}>
      <span className={cn('w-1.5 h-1.5 rounded-full', s.dot)} />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* PLAN BADGE                                                                 */
/* ═══════════════════════════════════════════════════════════════════════════ */

const PLAN_STYLES: Record<string, string> = {
  free:          'bg-surface-200 text-surface-500',
  business_lite: 'badge-blue',
  business:      'badge-violet',
  enterprise:    'badge-brand',
};

export function PlanBadge({ plan, label }: { plan: string; label?: string }) {
  return (
    <span className={cn('inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold',
      PLAN_STYLES[plan] || PLAN_STYLES.free)}>
      {label || plan.charAt(0).toUpperCase() + plan.slice(1).replace(/_/g, ' ')}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* MODAL                                                                      */
/* ═══════════════════════════════════════════════════════════════════════════ */

export function Modal({ open, onClose, title, subtitle, children, wide, size = 'default' }: {
  open: boolean; onClose: () => void; title: string; subtitle?: string;
  children: ReactNode;
  /** Legacy: same as size='wide'. Prefer `size`. */
  wide?: boolean;
  /** Width preset. default = max-w-lg, wide = max-w-2xl, xl = max-w-4xl, full = max-w-6xl. */
  size?: 'default' | 'wide' | 'xl' | 'full';
}) {
  const overlay = useRef<HTMLDivElement>(null);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);
  if (!open) return null;
  return (
    <div ref={overlay} className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={e => {
        // Close when clicking backdrop (anything outside the modal card)
        const card = overlay.current?.querySelector('[data-modal-card]');
        if (card && !card.contains(e.target as Node)) onClose();
      }}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />
      <div data-modal-card className={cn('relative w-full bg-surface-0 border border-overlay/[0.06] rounded-2xl p-6 animate-scale-in shadow-elevated max-h-[90vh] overflow-y-auto',
        // Legacy `wide` flag still wins if size is the default.
        (size === 'default' && wide) ? 'max-w-2xl'
          : size === 'wide' ? 'max-w-2xl'
          : size === 'xl'   ? 'max-w-4xl'
          : size === 'full' ? 'max-w-6xl'
          : 'max-w-lg')}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="text-lg font-semibold text-surface-900">{title}</h2>
            {subtitle && <p className="text-sm text-surface-500 mt-1">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-surface-200 text-surface-500 hover:text-surface-900 transition-colors shrink-0 ml-4"
            aria-label="Close"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* TOGGLE                                                                     */
/* ═══════════════════════════════════════════════════════════════════════════ */

export function Toggle({ enabled, onChange, label, description, disabled }: {
  enabled: boolean; onChange: (v: boolean) => void;
  label?: string; description?: string; disabled?: boolean;
}) {
  return (
    <label className={cn('flex items-center justify-between gap-3 py-1', disabled && 'opacity-40')}>
      {(label || description) && (
        <div className="flex-1 min-w-0">
          {label && <div className="text-sm font-medium text-surface-800">{label}</div>}
          {description && <div className="text-xs text-surface-500 mt-0.5">{description}</div>}
        </div>
      )}
      <button type="button" role="switch" aria-checked={enabled} disabled={disabled}
        onClick={() => onChange(!enabled)}
        className={cn('relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors cursor-pointer',
          enabled ? 'bg-brand-500 shadow-glow-sm' : 'bg-surface-300', disabled && 'cursor-not-allowed')}>
        <span className={cn('block h-5 w-5 rounded-full bg-white shadow-sm transform transition-transform mt-0.5',
          enabled ? 'translate-x-5 ml-0.5' : 'translate-x-0.5')} />
      </button>
    </label>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* SEARCH INPUT                                                               */
/* ═══════════════════════════════════════════════════════════════════════════ */

export function SearchInput({ value, onChange, placeholder = 'Search...' }: {
  value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <div className="relative">
      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input type="text" value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} className="input-field pl-10 py-2" />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* EMPTY STATE                                                                */
/* ═══════════════════════════════════════════════════════════════════════════ */

export function EmptyState({ icon, title, description, action }: {
  icon: ReactNode; title: string; description: string; action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="p-4 rounded-2xl bg-surface-200/40 text-surface-500 mb-4">{icon}</div>
      <h3 className="text-lg font-medium text-surface-900 mb-1">{title}</h3>
      <p className="text-sm text-surface-500 text-center max-w-sm mb-6">{description}</p>
      {action}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* SPINNER                                                                    */
/* ═══════════════════════════════════════════════════════════════════════════ */

export function Spinner({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const s = { sm: 'w-4 h-4', md: 'w-6 h-6', lg: 'w-8 h-8' }[size];
  return (
    <svg className={cn('animate-spin text-brand-500', s)} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* DROPDOWN                                                                   */
/* ═══════════════════════════════════════════════════════════════════════════ */

export function Dropdown({ trigger, children, align = 'right' }: {
  trigger: ReactNode; children: ReactNode; align?: 'left' | 'right';
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>({});

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const s: CSSProperties = { position: 'fixed', top: rect.bottom + 4, zIndex: 9999 };
      if (align === 'right') {
        s.right = window.innerWidth - rect.right;
      } else {
        s.left = rect.left;
      }
      setStyle(s);
    }
  }, [open, align]);

  return (
    <div ref={triggerRef}>
      <div onClick={() => setOpen(!open)}>{trigger}</div>
      {open && createPortal(
        <div ref={menuRef}
          className="min-w-[180px] bg-surface-0 border border-overlay/[0.06] rounded-xl shadow-elevated p-1 animate-scale-in"
          style={style}
          onClick={() => setOpen(false)}>
          {children}
        </div>,
        document.body
      )}
    </div>
  );
}

export function DropdownItem({ children, onClick, danger }: {
  children: ReactNode; onClick?: () => void; danger?: boolean;
}) {
  return (
    <button onClick={onClick} className={cn('w-full text-left px-3 py-2 text-sm rounded-lg transition-colors',
      danger ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-50 dark:hover:bg-red-50 dark:bg-red-900/20' : 'text-surface-700 hover:bg-surface-200')}>
      {children}
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/* TABS                                                                       */
/* ═══════════════════════════════════════════════════════════════════════════ */

export function Tabs({ tabs, active, onChange }: {
  tabs: { id: string; label: string; count?: number }[];
  active: string; onChange: (id: string) => void;
}) {
  return (
    <div className="flex gap-1 border-b border-overlay/[0.06]">
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)}
          className={cn('px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
            active === t.id ? 'text-brand-600 dark:text-brand-400 border-brand-500' : 'text-surface-500 border-transparent hover:text-surface-800')}>
          {t.label}
          {t.count !== undefined && (
            <span className="ml-2 text-xs bg-surface-100 px-1.5 py-0.5 rounded-md">{t.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}
